import { apiClient } from './apiClient'
import { environment } from '../../config/environment'
import { StorageService } from '../firebase/storageService'
import type { Certification } from '../../types/certification'
import { logger } from '../../utils/logger'

export class CertificationsService {
  /**
   * Get all certifications
   */
  static async getCertifications(): Promise<Certification[]> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await CertificationsService.getMockCertifications()
    }
    
    logger.log('📦 Fetching certifications from API...')
    const response = await apiClient.get<Certification[]>('/v1/certifications')
    logger.log('✅ Certifications received:', response)
    
    // The API returns an array directly
    if (!Array.isArray(response)) {
      return []
    }

    // Transform imageURL from Firebase Storage path to download URL
    const certificationsWithUrls = await Promise.all(
      response.map(async (cert) => {
        if (cert.imageURL) {
          try {
            // Get the download URL from Firebase Storage
            const downloadUrl = await StorageService.getDownloadURL(cert.imageURL)
            return {
              ...cert,
              imageURL: downloadUrl
            }
          } catch (error) {
            logger.error(`Failed to get download URL for certification ${cert.name}:`, error)
            // Return certification without image URL if it fails
            return cert
          }
        }
        return cert
      })
    )
    
    return certificationsWithUrls
  }

  /**
   * Create a new certification
   */
  static async createCertification(certification: Omit<Certification, 'id'>): Promise<Certification> {
    const createdCertification = await apiClient.post<Certification>('/v1/certifications', certification)
    return createdCertification
  }

  /**
   * Update an existing certification
   */
  static async updateCertification(certification: Certification): Promise<Certification> {
    if (!certification.id) {
      throw new Error('Certification ID is required for update')
    }
    const updatedCertification = await apiClient.put<Certification>(`/v1/certifications/${certification.id}`, certification)
    return updatedCertification
  }

  /**
   * Delete a certification
   */
  static async deleteCertification(id: number): Promise<void> {
    await apiClient.delete(`/v1/certifications/${id}`)
  }

  /**
   * Mock data for development/testing
   */
  private static async getMockCertifications(): Promise<Certification[]> {
    const mockCertifications: Certification[] = [
      {
        id: 1,
        name: 'Ecocert',
        priority: 1,
        imageURL: 'https://via.placeholder.com/70'
      },
      {
        id: 2,
        name: 'Cosmebio',
        priority: 2,
        imageURL: 'https://via.placeholder.com/70'
      },
      {
        id: 3,
        name: 'Bio EU',
        priority: 3,
        imageURL: 'https://via.placeholder.com/70'
      },
      {
        id: 4,
        name: 'Vegan',
        priority: 4,
        imageURL: 'https://via.placeholder.com/70'
      },
      {
        id: 5,
        name: 'Cruelty Free',
        priority: 5,
        imageURL: 'https://via.placeholder.com/70'
      }
    ]
    
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockCertifications), 300)
    })
  }
}